class ProductComparison extends HTMLElement {
  connectedCallback() {
    this.setupUniformRowHeights();
    this.setupResizeObserver();
    this.setAttribute('data-init', 'true');
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  /**
   * Setup uniform row heights by calculating the tallest row
   */
  setupUniformRowHeights() {
    // Find all header elements - looking for the header divs that contain the company names/images
    this.headerElements = this.querySelectorAll(
      '.comparison-table [role="columnheader"] > div:nth-child(1)'
    );

    // Find feature rows by column
    this.featureNameElements = this.querySelectorAll('[role="rowheader"]');
    this.companyCells = this.querySelectorAll(
      '.comparison-table [role="columnheader"]:nth-child(2) [role="cell"]'
    );
    this.competitor1Cells = this.querySelectorAll(
      '.comparison-table [role="columnheader"]:nth-child(3) [role="cell"]'
    );
    this.competitor2Cells = this.querySelectorAll(
      '.comparison-table [role="columnheader"]:nth-child(4) [role="cell"]'
    );

    if (this.headerElements.length === 0 && this.featureNameElements.length === 0) return;

    // Group rows by their position (each feature has 4 cells: feature name + 3 products)
    this.rowGroups = this.groupRowsByFeature();

    // Calculate and apply uniform heights for each row group
    this.applyUniformHeights();
  }

  /**
   * Group rows by feature (matching elements at the same index across columns)
   */
  groupRowsByFeature() {
    const groups = [];

    // First, add headers as the first group if they exist
    if (this.headerElements.length > 0) {
      groups.push(Array.from(this.headerElements));
    }

    // Group feature rows by matching index positions across columns
    const featureCount = this.featureNameElements.length;

    for (let i = 0; i < featureCount; i++) {
      const rowGroup = [];

      // Add feature name (column 1)
      if (this.featureNameElements[i]) {
        rowGroup.push(this.featureNameElements[i]);
      }

      // Add company cell (column 2)
      if (this.companyCells[i]) {
        rowGroup.push(this.companyCells[i]);
      }

      // Add competitor 1 cell (column 3)
      if (this.competitor1Cells[i]) {
        rowGroup.push(this.competitor1Cells[i]);
      }

      // Add competitor 2 cell (column 4)
      if (this.competitor2Cells[i]) {
        rowGroup.push(this.competitor2Cells[i]);
      }

      // Only add complete rows
      if (rowGroup.length === 4) {
        groups.push(rowGroup);
      }
    }

    return groups;
  }

  /**
   * Calculate and apply uniform heights to all row groups
   */
  applyUniformHeights() {
    this.rowGroups.forEach((rowGroup, index) => {
      // Reset inline styles to let CSS classes work
      rowGroup.forEach((row) => {
        row.style.height = '';
        row.style.minHeight = '';
      });

      // Force reflow to get accurate measurements
      void rowGroup[0].offsetHeight;

      // Calculate the maximum height in this row group
      let maxHeight = 0;
      rowGroup.forEach((row) => {
        const actualHeight = row.getBoundingClientRect().height;
        maxHeight = Math.max(maxHeight, Math.ceil(actualHeight));
      });

      // Apply the height to all cells in the row
      rowGroup.forEach((row) => {
        // For headers (first row), just sync the heights
        if (index === 0) {
          row.style.minHeight = `${maxHeight}px`;
        } else {
          // For feature rows, apply minHeight for consistent alignment
          row.style.minHeight = `${maxHeight}px`;
        }
      });
    });
  }

  /**
   * Setup ResizeObserver to recalculate heights when content changes
   */
  setupResizeObserver() {
    if (!window.ResizeObserver) return;

    this.resizeObserver = new ResizeObserver(() => {
      // Debounce the resize handler
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.applyUniformHeights();
      }, 100);
    });

    // Observe the comparison table container
    const tableContainer = this.querySelector('.comparison-table');
    if (tableContainer) {
      this.resizeObserver.observe(tableContainer);
    }
  }
}

customElements.define('product-comparison', ProductComparison);
